import { format } from 'date-fns';
import type { QuestionTypes } from '@/types';
import { useTranslation } from 'react-i18next';
import { useFormResponseStore } from '@/store';
import React, { useEffect, useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TimePicker } from '@/components/ui/time-picker';
import { formatInTimezone, parseDateInTimezone } from '@/utils/timezone';

interface DateProps {
    question: QuestionTypes;
    sectionId: string;
    sectionName: string;
    setValidationErrors: (sectionId: string, questionId: string, error: string | null) => void;
}

export const DateTimeAnswer: React.FC<DateProps> = ({ question, sectionId, sectionName, setValidationErrors }) => {
    const { t } = useTranslation();
    const { setResponse, formResponses } = useFormResponseStore();
    const existingSection = formResponses[sectionId];
    const existingResponse = existingSection?.questions[question.id]?.response || null;

    const [singleDate, setSingleDate] = useState<Date | undefined>(() => {
        if (existingResponse) {
            if (typeof existingResponse === 'string') {
                const date = new Date(existingResponse);
                return !isNaN(date.getTime()) ? date : undefined;
            } else if (existingResponse.date) {
                const date = new Date(existingResponse.date);
                return !isNaN(date.getTime()) ? date : undefined;
            } else if (existingResponse.start) {
                const date = new Date(existingResponse.start);
                return !isNaN(date.getTime()) ? date : undefined;
            }
        }
        return undefined;
    });
    const [timeValue, setTimeValue] = useState<string>(() => {
        if (existingResponse) {
            if (typeof existingResponse === 'string') {
                return '';
            } else if (existingResponse.time) {
                return existingResponse.time;
            }
        }
        return '';
    });

    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    const handleDateSelect = (date: Date | undefined) => {
        setSingleDate(date);
        if (date) {
            setIsCalendarOpen(false);
            // Validation only clears once the time has been chosen too
            if (timeValue) {
                setValidationErrors(sectionId, question.id, null);
            }
        }
    };
    // Initial validation on mount - runs ONCE like other components
    useEffect(() => {
        if (question.required === 'yes') {
            if (!singleDate && !timeValue) {
                setValidationErrors(sectionId, question.id, `${question.label} ${t('forms.isRequired')}`);
            } else {
                setValidationErrors(sectionId, question.id, null);
            }
        }
    }, []); // Empty deps - runs only on mount

    useEffect(() => {
        if (singleDate && timeValue) {
            const response = {
                date: format(singleDate, 'yyyy-MM-dd'),
                time: timeValue,
            };
            setResponse(sectionId, sectionName, question.id, question.type, question.label ?? '', response);
        } else if (singleDate) {
            const response = format(singleDate, 'yyyy-MM-dd');
            setResponse(sectionId, sectionName, question.id, question.type, question.label ?? '', response);
        } else if (timeValue) {
            const response = timeValue;
            setResponse(sectionId, sectionName, question.id, question.type, question.label ?? '', response);
        }
    }, [singleDate, timeValue]);

    const handleTimeChange = (value: string) => {
        setTimeValue(value);
        // Clear validation error when time is selected
        if (value && singleDate) {
            setValidationErrors(sectionId, question.id, null);
        }
    };

    const formatDateForDisplay = (date: Date) => {
        if (!question.timezone) return format(date, 'LLL dd, y');
        
        try {
            return formatInTimezone(date, question.timezone, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return format(date, 'LLL dd, y');
        }
    };

    const getCurrentTime = () => {
        const now = new Date();
        return now.toLocaleTimeString('en-US', { 
            hour12: true, 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const getMidnightTime = () => {
        const midnight = new Date();
        midnight.setHours(23, 59, 0, 0);
        return midnight.toLocaleTimeString('en-US', { 
            hour12: true, 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const renderDateTimeInput = () => {
        switch (question.timeType) {
            case 'all-time':
                return (
                    <div className="space-y-3 mt-1">
                        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    className='rounded border-l-[2.4px] border-primary px-5 py-3 flex justify-between items-center text-[#5A5A5A] placeholder:text-[#5A5A5A] bg-[#F6F9FF] text-sm w-full outline-none hover:bg-[#E8F2FF] transition-colors'
                                >
                                    <div className='flex items-center gap-2'>
                                        <CalendarIcon className="h-4 w-4" />
                                        {singleDate ? <span>{formatDateForDisplay(singleDate)}</span> : <span>{t('forms.pickADate')}</span>}
                                    </div>
                                    <CalendarIcon className="h-4 w-4" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={singleDate}
                                    onSelect={handleDateSelect}
                                    captionLayout="dropdown-buttons"
                                    fromYear={1900}
                                    toYear={2050}
                                />
                            </PopoverContent>
                        </Popover>
                        <TimePicker
                            value={timeValue}
                            onChange={handleTimeChange}
                            placeholder={t('forms.pickATime')}
                            format="12h"
                            timezone={question.timezone}
                        />
                    </div>
                );
            case 'past-only':
                return (
                    <div className="space-y-3 mt-1">
                        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    className='rounded border-l-[2.4px] border-primary px-5 py-3 flex justify-between items-center text-[#5A5A5A] placeholder:text-[#5A5A5A] bg-[#F6F9FF] text-sm w-full outline-none hover:bg-[#E8F2FF] transition-colors'
                                >
                                    <div className='flex items-center gap-2'>
                                        <CalendarIcon className="h-4 w-4" />
                                        {singleDate ? <span>{formatDateForDisplay(singleDate)}</span> : <span>{t('forms.pickADate')}</span>}
                                    </div>
                                    <CalendarIcon className="h-4 w-4" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={singleDate}
                                    onSelect={handleDateSelect}
                                    captionLayout="dropdown-buttons"
                                    toDate={new Date()}
                                    fromYear={1900}
                                />
                            </PopoverContent>
                        </Popover>
                        <TimePicker
                            value={timeValue}
                            onChange={handleTimeChange}
                            placeholder={t('forms.pickATime')}
                            format="12h"
                            maxTime={getCurrentTime()}
                        />
                    </div>
                );
            case 'future-only':
                return (
                    <div className="space-y-3 mt-1">
                        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    className='rounded border-l-[2.4px] border-primary px-5 py-3 flex justify-between items-center text-[#5A5A5A] placeholder:text-[#5A5A5A] bg-[#F6F9FF] text-sm w-full outline-none hover:bg-[#E8F2FF] transition-colors'
                                >
                                    <div className='flex items-center gap-2'>
                                        <CalendarIcon className="h-4 w-4" />
                                        {singleDate ? <span>{formatDateForDisplay(singleDate)}</span> : <span>{t('forms.pickADate')}</span>}
                                    </div>
                                    <CalendarIcon className="h-4 w-4" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={singleDate}
                                    onSelect={handleDateSelect}
                                    captionLayout="dropdown-buttons"
                                    fromDate={new Date()}
                                    fromYear={1900}
                                    toYear={2100}
                                />
                            </PopoverContent>
                        </Popover>
                        <TimePicker
                            value={timeValue}
                            onChange={handleTimeChange}
                            placeholder={t('forms.pickATime')}
                            format="12h"
                            minTime={getCurrentTime()}
                            maxTime={getMidnightTime()}
                        />
                    </div>
                );
            default:
                return <span>{t('forms.pleaseSelectValidDateType')}</span>;
        }
    };

    return (
        <div className="mb-4">
            <div className="main-label">
                {question.label} {question.required == 'yes' && <span className="text-red-500">*</span>}
            </div>
            {renderDateTimeInput()}
        </div>
    );
};